import { useEffect, useRef } from "react"
import { Check, LayoutGrid, Maximize2, Minimize2, PictureInPicture2, Presentation, Settings, SmilePlus, Sparkles } from "lucide-react"
import { NetworkQualityIcon, describeNetworkThreshold, networkThresholdTone, type NetworkThreshold } from "./NetworkPanel"
import type { CallSettingsPane } from "./CallSettingsDialog"
import type { StageViewMode } from "./Stage"

type MoreMenuProps = {
  viewMode: StageViewMode
  onChangeViewMode: (mode: StageViewMode) => void
  isFullscreen: boolean
  /** Full screen is hidden on compact layouts, where the browser chrome already fills the phone. */
  fullscreenAvailable: boolean
  onToggleFullscreen: () => void
  pipAvailable: boolean
  onEnterPictureInPicture: () => void
  onOpenSettings: (pane: CallSettingsPane) => void
  /** Phones: the reaction and network buttons that do not fit in the bar or header live here. */
  onOpenReactions?: () => void
  networkThreshold?: NetworkThreshold
  onOpenNetwork?: () => void
  /** The mobile app's native calls have no background effects. */
  effectsAvailable?: boolean
  onClose: () => void
}

const itemClass = "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left hover:bg-white/10"

/**
 * The three-dot menu next to the call controls, modelled on Google Meet's: change layout,
 * full screen, open picture-in-picture, backgrounds and effects, and Settings. Preferences
 * such as automatic picture-in-picture live in Settings > General.
 */
export function MoreMenu({ viewMode, onChangeViewMode, isFullscreen, fullscreenAvailable, onToggleFullscreen, pipAvailable, onEnterPictureInPicture, onOpenSettings, onOpenReactions, networkThreshold = "good", onOpenNetwork, effectsAvailable = true, onClose }: MoreMenuProps) {
  const firstRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  useEffect(() => {
    firstRef.current?.focus()
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCloseRef.current()
    }
    const handleOutside = (event: Event) => {
      const target = event.target
      if (!(target instanceof Node) || menuRef.current?.contains(target)) return
      // Let the trigger's own toggle close it; closing on pointerdown there would reopen it on click.
      if (target instanceof Element && target.closest('[data-testid="call-more-options"]')) return
      onCloseRef.current()
    }
    document.addEventListener("keydown", handleKey)
    document.addEventListener("pointerdown", handleOutside, true)
    document.addEventListener("focusin", handleOutside)
    return () => {
      document.removeEventListener("keydown", handleKey)
      document.removeEventListener("pointerdown", handleOutside, true)
      document.removeEventListener("focusin", handleOutside)
    }
  }, [])
  const run = (action: () => void) => () => {
    action()
    onClose()
  }

  return (
    <div
      ref={menuRef}
      role="menu"
      aria-label="More options"
      data-testid="call-more-menu"
      className="absolute bottom-24 left-1/2 z-[45] w-[min(18rem,calc(100vw-1.5rem))] -translate-x-1/2 rounded-2xl bg-[#0f1419] p-2 text-sm text-white shadow-2xl ring-1 ring-white/10"
    >
      <div role="group" aria-label="Change layout" className="mb-1 border-b border-white/10 pb-1">
        <p className="px-3 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-white/50">Layout</p>
        {([
          { id: "speaker" as const, label: "Speaker view", icon: <Presentation className="h-4 w-4 text-white/70" /> },
          { id: "grid" as const, label: "Grid view", icon: <LayoutGrid className="h-4 w-4 text-white/70" /> },
        ]).map((option, index) => {
          const checked = option.id === viewMode
          return (
            <button
              key={option.id}
              ref={index === 0 ? firstRef : undefined}
              type="button"
              role="menuitemradio"
              aria-checked={checked}
              data-testid={`call-layout-${option.id}`}
              onClick={run(() => onChangeViewMode(option.id))}
              className={itemClass}
            >
              {option.icon}
              <span className="flex-1">{option.label}</span>
              {checked && <Check className="h-4 w-4 text-[#f6a35c]" />}
            </button>
          )
        })}
      </div>
      {onOpenReactions && (
        <button type="button" role="menuitem" data-testid="call-menu-reactions" onClick={run(onOpenReactions)} className={itemClass}>
          <SmilePlus className="h-4 w-4 text-white/70" />
          Send a reaction
        </button>
      )}
      {fullscreenAvailable && (
        <button type="button" role="menuitem" data-testid="call-menu-fullscreen" onClick={run(onToggleFullscreen)} className={itemClass}>
          {isFullscreen ? <Minimize2 className="h-4 w-4 text-white/70" /> : <Maximize2 className="h-4 w-4 text-white/70" />}
          {isFullscreen ? "Exit full screen" : "Full screen"}
        </button>
      )}
      {pipAvailable && (
        <button type="button" role="menuitem" data-testid="call-enter-pip" onClick={run(onEnterPictureInPicture)} className={itemClass}>
          <PictureInPicture2 className="h-4 w-4 text-white/70" />
          Open picture-in-picture
        </button>
      )}
      {effectsAvailable && (
        <button type="button" role="menuitem" data-testid="call-menu-effects" onClick={run(() => onOpenSettings("effects"))} className={itemClass}>
          <Sparkles className="h-4 w-4 text-white/70" />
          Backgrounds and effects
        </button>
      )}
      {onOpenNetwork && (
        <button type="button" role="menuitem" data-testid="call-menu-network" onClick={run(onOpenNetwork)} className={itemClass}>
          <NetworkQualityIcon threshold={networkThreshold} className={`h-4 w-4 ${networkThresholdTone(networkThreshold)}`} />
          <span className="flex-1">Network quality</span>
          <span className="text-xs text-white/50">{describeNetworkThreshold(networkThreshold)}</span>
        </button>
      )}
      <div className="my-1 border-t border-white/10" />
      <button type="button" role="menuitem" data-testid="call-menu-settings" onClick={run(() => onOpenSettings("audio"))} className={itemClass}>
        <Settings className="h-4 w-4 text-white/70" />
        Settings
      </button>
    </div>
  )
}
